import React, { useCallback } from "react";
import Select, { OptionTypeBase } from "react-select";

import { Product } from "../../../interfaces";
import { useStringArraySearchParam } from "../../../hooks";

interface Props {
  products: Product[];
  onSelect: { (ingredient: string[]): void };
  onRemove: { (ingredient: string): void };
  onClear: { (): void };
}

function otherIngredientToOption(ingredient: string): OptionTypeBase {
  return {
    label: ingredient.toLowerCase(),
    value: ingredient.toLowerCase(),
  };
}

export default function InactiveIngredientSelect(props: Props) {
  const { products, onSelect, onRemove, onClear } = props;
  const [excludeInactiveIngredients] = useStringArraySearchParam("-otherIngredients");
  const onChange = useCallback(
    (nextValue, { action, removedValue }) => {
      switch (action) {
        case "select-option":
          onSelect(nextValue.map(({ value }: { value: string }) => encodeURIComponent(value).toLowerCase()));
          break;
        case "remove-value":
        case "pop-value":
          onRemove(encodeURIComponent(removedValue.value).toLowerCase());
          break;
        case "clear":
          onClear();
          break;
      }
    },
    [onSelect, onRemove, onClear]
  );

  const otherIngredientsLookup = new Set<string>();
  const otherIngredientsFiltered = ((products ?? [])
    .map(({ otherIngredients }) => otherIngredients)
    .filter((ingredients) => ingredients) as string[][])
    .reduce((acc, ingredients) => {
      return acc.concat(ingredients);
    }, [])
    .map((otherIngredient) => otherIngredient.trim())
    .reduce((acc, otherIngredient) => {
      if (otherIngredientsLookup.has(otherIngredient.toLowerCase())) {
        return acc;
      }

      otherIngredientsLookup.add(otherIngredient.toLowerCase());
      return acc.concat([otherIngredient]);
    }, [] as string[]);

  const options = otherIngredientsFiltered.map(otherIngredientToOption);
  const currentOptions = excludeInactiveIngredients.map(decodeURIComponent).map(otherIngredientToOption);
  const allOptions = currentOptions.concat(options).sort((a, b) => a.value.localeCompare(b.value));

  return <Select onChange={onChange} options={allOptions} value={currentOptions} isMulti isSearchable isClearable />;
}
